import {dailyRate} from 'utils/dailyRates';
import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  OutlinedInput,
  Toolbar,
  Typography,
} from '@mui/material';
import { TimeField } from '@mui/x-date-pickers';

const styles = {
  impView: {
    display: 'inline-block',
    width: '75px',
    fontSize: '.9em',
    boxSizing: 'border-box',
    borderRadius: '4px',
    padding: '4px',
    marginRight: '5px',
    textAlign: 'right',
    backgroundColor: theme => theme.vars.palette.app.scheduleValueBackground,
  },
  dpView: {
    display: 'inline-block',
    width: '40px',
    fontSize: '12px',
    boxSizing: 'border-box',
    borderRadius: '3px',
    padding: '3px',
    color: 'text.secondary',
  },
  timeField: {
    '& .MuiInputBase-root': {
      width: '40px',
      '& input': {
        fontSize: '12px',
        padding: '3px',
      }
    },
    '& .MuiInputBase-input': {p: '5px'},
  },
  timeFieldDialog: {
    '& .MuiInputBase-root': {
      width: '50px',
      '& input': {
        fontSize: '12px',
        padding: '6px',
      }
    },
    '& .MuiInputBase-input': {p: '5px'},
  },
};

const DayCard = ({day, imps, dps, ctr, overrun, isPast, isSelected, onChange, onClick}) => {
  const [editing, setEditing] = useState('');

  const handleEdit = (ev, field) => {
    ev.stopPropagation();
    isPast || setEditing(field);
  };
  // prevent text selection (browser default behavior)
  const handleMouseDown = (ev) => ev.shiftKey && ev.preventDefault();

  const dt = day.format('YYYYMMDD');
  return (
    <Card
      sx={{
        padding: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: theme => isPast
          ? theme.vars.palette.app.schedulePastBackground
          : isSelected
            ? theme.vars.palette.app.scheduleSelectedBackground
            : theme.vars.palette.background.paper,
      }}
      onClick={(ev) => isPast || onClick(ev, dt)}
      onMouseDown={handleMouseDown}
    >
      <Box>
        <Typography
          component="h3"
          gutterBottom
          variant="overline"
          sx={{textTransform: 'none'}}
        >
          {day.format('MM-DD')}
        </Typography>
        { editing === 'imp' ? (
        <OutlinedInput
          value={imps}
          margin="dense"
          size="small"
          autoFocus={true}
          sx={{fontSize: '.9em', width: '75px'}}
          inputProps={{ style: {padding: '4px', textAlign: 'right'} }}
          onChange={ev => onChange('daily_imps', dt, ev.target.value)}
          onFocus={ev => ev.target.select()}
          onBlur={() => setEditing('')}
        />
        ) : (
        <Typography
          sx={styles.impView}
          onClick={(ev) => handleEdit(ev, 'imp')}
        >{imps}</Typography>
        )}
        { dps && (
        <Box sx={{
          m: '3px -3px 0',
          borderRadius: '3px',
          backgroundColor: 'background.paper',
        }}>
          { editing === 'dp0' ? (
            <TimeField
              sx={styles.timeField}
              format="HH:mm"
              value={dps[0]}
              onChange={val => onChange('dayparts', dt, [val, dps[1]])}
              inputProps={{ onBlur: () => setEditing('') }}
              autoFocus
            />
          ) : (
            <span
              style={styles.dpView}
              onClick={(ev) => handleEdit(ev, 'dp0')}
            >{dps[0].format('HH:mm')}</span>
          )}
          <span style={{margin: "0 2px"}}>~</span>
          { editing === 'dp1' ? (
            <TimeField
              sx={styles.timeField}
              format="HH:mm"
              value={dps[1]}
              onChange={val => onChange('dayparts', dt, [dps[0], val])}
              inputProps={{ onBlur: () => setEditing('') }}
              autoFocus
            />
          ) : (
            <span
              style={styles.dpView}
              onClick={(ev) => handleEdit(ev, 'dp1')}
            >{dps[1].format('HH:mm')}</span>
          )}
        </Box>)}
        {[
          ['daily_ctr', 'CTR', ctr],
          ['daily_overrun', '溢出', overrun],
        ].filter(([, , value]) => value !== undefined).map(([field, label, value]) => (
          <Box key={field} sx={{mt: '3px', fontSize: '12px', whiteSpace: 'nowrap'}}>
            <span>{label}: </span>
            {editing === field ? (
              <OutlinedInput value={value ?? ''} size="small" autoFocus sx={{fontSize: '12px', width: '60px'}}
                inputProps={{style: {padding: '3px', textAlign: 'right'}, 'aria-label': `${dt} ${label}`}}
                onChange={ev => onChange(field, dt, ev.target.value)}
                onFocus={ev => ev.target.select()} onBlur={() => setEditing('')} />
            ) : (
              <span onClick={ev => handleEdit(ev, field)}>{value ?? '未配置'}</span>
            )}{value == null ? '' : '%'}
          </Box>
        ))}
      </Box>
    </Card>
  );
};

const BatchEditor = ({editDayparts, editCtr, editOverrun, onChange, onClose}) => {
  const [vals, setVals] = useState({imps: 0, ctr: 0, overrun: 20, ds: dayjs('2022-02-22 00:00'), de: dayjs('2022-02-22 23:59')});
  const [editing, setEditing] = useState(editDayparts || editCtr || editOverrun ? 0 : 1);  // 1: imps, 2: dayparts, 4: ctr, 8: overrun

  const handleChange = () => {
    const changed = {};
    if ((editing & 1) > 0) {
      changed.imps = vals.imps;
    }
    if ((editing & 2) > 0) {
      changed.ds = vals.ds;
      changed.de = vals.de;
    }
    if ((editing & 4) > 0) changed.ctr = vals.ctr;
    if ((editing & 8) > 0) changed.overrun = vals.overrun;
    onChange(changed);
  };

  return (
      <Dialog open={true} onClose={onClose}>
        <DialogContent>
          <Box>
            <Typography component="span" sx={{mr: 1}}>展示:</Typography>
            {(editing & 1) > 0 ? (
            <OutlinedInput
              value={vals.imps}
              margin="dense"
              size="small"
              sx={{fontSize: '.9em', width: '75px'}}
              inputProps={{ style: {padding: '4px', textAlign: 'right'} }}
              onChange={ev => setVals(prev => ({...prev, imps: ev.target.value}))}
            />
            ) : (
            <Button variant="text" onClick={() => setEditing(prev => (prev|1))}>修改</Button>
            )}
          </Box>
          {editDayparts && (
          <Box sx={{mt: 1, display: 'flex', alignItems: 'center'}}>
            <Typography component="span" sx={{mr: 1}}>时段:</Typography>
            {(editing & 2) > 0 ? (<>
            <TimeField
              sx={styles.timeFieldDialog}
              format="HH:mm"
              value={vals.ds}
              onChange={val => setVals(prev => ({...prev, ds: val}))}
            />
            <Typography component="span" sx={{mx: '3px'}}>~</Typography>
            <TimeField
              sx={styles.timeFieldDialog}
              format="HH:mm"
              value={vals.de}
              onChange={val => setVals(prev => ({...prev, de: val}))}
            />
            </>) : (
            <Button variant="text" onClick={() => setEditing(prev => (prev|2))}>修改</Button>
            )}
          </Box>
          )}
          {[
            [editCtr, 4, 'ctr', 'CTR'],
            [editOverrun, 8, 'overrun', '溢出'],
          ].filter(([enabled]) => enabled).map(([, mask, field, label]) => (
            <Box key={field} sx={{mt: 1}}>
              <Typography component="span" sx={{mr: 1}}>{label}(%):</Typography>
              {(editing & mask) > 0 ? (
                <OutlinedInput value={vals[field]} size="small" sx={{fontSize: '.9em', width: '75px'}}
                  inputProps={{style: {padding: '4px', textAlign: 'right'}, 'aria-label': `批量${label}`}}
                  onChange={ev => setVals(prev => ({...prev, [field]: ev.target.value}))} />
              ) : (
                <Button variant="text" onClick={() => setEditing(prev => prev | mask)}>修改</Button>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>取消</Button>
          <Button onClick={handleChange}>确认</Button>
        </DialogActions>
      </Dialog>
  );
}

const fmtymd = (s) => [s.slice(0, 4), s.slice(4, 6), s.slice(6)].join('-');
function getDatesBetween(start, end) {
  if (start > end) [start, end] = [end, start];
  if (start === end) return [start];
  const sd = dayjs(fmtymd(start));
  const ed = dayjs(fmtymd(end));
  const res = [];
  for (let day = sd; !day.isAfter(ed, 'day'); day = day.add(1, 'day')) {
    res.push(day.format('YYYYMMDD'));
  }
  return res;
}

const ScheduleEditor = ({onChange, startDate, endDate, dailyImps, dayparts, dailyCtr = {}, dailyOverrun = {}, businessDateShift = 0}) => {
  const [selected, setSelected] = useState(new Set());
  const [lastClicked, setLastClicked] = useState(null);
  const [batchEdit, setBatchEdit] = useState(false);

  const showDayparts = Object.keys(dayparts).length > 0;
  const showCtr = Object.keys(dailyCtr).length > 0;
  const showOverrun = Object.keys(dailyOverrun).length > 0;

  const handleSelect = (ev, day) => {
    const newSet = new Set(selected);
    if (ev.shiftKey) {
      if (lastClicked === null) return;
      getDatesBetween(day, lastClicked).forEach((d) => {
        if (d === lastClicked) return;
        if (selected.has(d)) {
          newSet.delete(d);
        } else {
          newSet.add(d);
        }
      });
      setLastClicked(null);
    } else {
      if (selected.has(day)) {
        newSet.delete(day);
      } else {
        newSet.add(day);
      }
      setLastClicked(day);
    }
    setSelected(newSet);
  };

  const handleUnselectAll = () => setSelected(new Set());
  const handleBatchEdit = () => setBatchEdit(true);
  const closeModal = () => setBatchEdit(false);
  const handleBatchChange = (vals) => {
    Array.from(selected).forEach(day => {
      'imps' in vals && onChange('daily_imps', day, vals.imps);
      'ds' in vals && onChange('dayparts', day, [vals.ds, vals.de]);
      'ctr' in vals && onChange('daily_ctr', day, vals.ctr);
      'overrun' in vals && onChange('daily_overrun', day, vals.overrun);
    });
    setBatchEdit(false);
    setSelected(new Set());
  };

  const today = dayjs().tz().format('YYYYMMDD');
  const data = [];
  const defaultImps = Number(dailyImps.default) || 0;
  for (let day = startDate; !day.isAfter(endDate, 'day'); day = day.add(1, 'day')) {
    const dt = day.format('YYYYMMDD');
    data.push({
      day,
      imps: dt in dailyImps ? dailyImps[dt] : defaultImps,
      dps: showDayparts ? (dayparts[dt] || dayparts.default) : null,
      ctr: showCtr ? dailyRate(dailyCtr, dt) : undefined,
      overrun: showOverrun ? dailyRate(dailyOverrun, dt) : undefined,
      isPast: day.subtract(businessDateShift, 'day').format('YYYYMMDD') < today,
      isSelected: selected.has(dt),
    });
  }

  return (
    <Box>
      <Toolbar sx={{mt: -1, justifyContent: 'center'}}>
        <Button
          sx={{ mx: 0.5 }}
          variant="outlined"
          size="small"
          onClick={handleBatchEdit}
          disabled={selected.size === 0}
        >批量修改</Button>
        <Button
          sx={{ mx: 0.5 }}
          variant="outlined"
          size="small"
          onClick={handleUnselectAll}
          disabled={selected.size === 0}
        >取消选择</Button>
      </Toolbar>
      <Grid sx={{mt: -2}} container spacing={2}>
      {data.map(row => (
        <Grid item lg={2} sm={4} xs={6} sx={{pl:'10px !important'}} key={row.day.format('MMDD')}>
          <DayCard {...row} onChange={onChange} onClick={handleSelect} />
        </Grid>
      ))}
      </Grid>
    {batchEdit && (
      <BatchEditor
        editDayparts={showDayparts}
        editCtr={showCtr}
        editOverrun={showOverrun}
        onClose={closeModal}
        onChange={handleBatchChange}
      />
    )}
    </Box>
  );
};

export default ScheduleEditor;
